import { useEffect, useRef, useState } from 'react';

import { App } from 'antd';
import { useTranslation } from 'react-i18next';

import { visitHistoryQueryKey } from 'scenes/VisitHistory/utils/queryKey';
import { ERROR_TOAST_SECONDS } from 'scenes/VisitHistory/utils/toast';

import { ApiErrorData } from 'models/ApiResponse';
import {
  TiendaPerfectaStoreVisitsInsights,
  TiendaPerfectaVisitHistoryParams,
} from 'models/TiendaPerfecta';

import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

const POLL_MS = 5000;

/**
 * Observaciones y competencia del rango. El BFF responde con lo que ya tiene y
 * sigue leyendo el detalle en segundo plano: mientras `progress.complete` sea
 * `false` se vuelve a preguntar cada pocos segundos.
 */
export function useStoreVisitInsights(
  params: TiendaPerfectaVisitHistoryParams | undefined
) {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const [insights, setInsights] =
    useState<TiendaPerfectaStoreVisitsInsights | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const queryKey = visitHistoryQueryKey(params) || null;
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const messageRef = useRef(message);
  messageRef.current = message;
  const fallback = useRef('');
  fallback.current = t('scenes.store_visits.errors.insights');

  useEffect(() => {
    const current = paramsRef.current;
    if (!queryKey || !current) return undefined;

    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setInsights(null);
    setFailed(false);
    setLoading(true);

    const load = async () => {
      const response = await TiendaPerfectaService.getStoreVisitInsights(
        current
      );
      if (!active) return;
      setLoading(false);
      if (response.success) {
        const data = response.data as TiendaPerfectaStoreVisitsInsights;
        setInsights(data);
        if (!data.progress.complete) timer = setTimeout(load, POLL_MS);
      } else {
        setFailed(true);
        const error = response.data as ApiErrorData;
        messageRef.current.error(
          (error?.message as string) || fallback.current,
          ERROR_TOAST_SECONDS
        );
      }
    };
    load();

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [queryKey]);

  return { insights, loading, failed };
}
